<template>
    <div ref="wrapper" :class="wysiwygClassList">
        <template v-if="shouldRender && editable && !hideToolbar">
            <template v-if="inline && !disabled">
                <bubble-menu v-if="editor" :editor="editor">
                    <app-wysiwyg-toolbar
                        :class="toolbarClassList"
                        :editor="editor"
                        :form="form"
                        :exclude="exclude"
                        :disabled="disabled"
                        :toggle-options="toggleOptions"
                        @save="$emit('save')"
                    />
                </bubble-menu>
            </template>
            <template v-else>
                <app-wysiwyg-toolbar
                    v-if="editor"
                    :class="toolbarClassList"
                    :editor="editor"
                    :form="form"
                    :exclude="exclude"
                    :disabled="disabled"
                    @save="$emit('save')"
                />
            </template>
        </template>

        <template v-if="showValidity && editable && editor">
            <v-chip class="app-wysiwyg__validity" x-small :color="editor.storage.Validator.valid ? 'success' : 'error'">
                {{ editor.storage.Validator.valid ? 'Valid' : 'Invalid' }}
            </v-chip>
        </template>

        <editor-content class="app-wysiwyg__content" :editor="editor" />
    </div>
</template>

<script>
import { createEditor, EditorContent, BubbleMenu } from '@/lib/tiptap';
import getKeys from '@/lib/helpers/getKeys';
import wait from '@/lib/helpers/wait';

export default {
    name: 'AppWysiwyg',
    components: {
        EditorContent,
        BubbleMenu,
        AppWysiwygToolbar: () =>
            import(
                /* webpackChunkName: "core" */
                '@/components/AppWysiwygToolbar'
            )
    },
    props: {
        inline: {
            type: Boolean,
            default: false
        },
        hideToolbar: {
            type: Boolean,
            default: false
        },
        value: {
            type: [Object, String],
            default: null
        },
        editable: {
            type: Boolean,
            default: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showValidity: {
            type: Boolean,
            default: false
        },
        form: {
            type: Boolean,
            default: false
        },
        exclude: {
            type: Array,
            default: () => ['save', 'undo', 'redo']
        }
    },
    data: () => ({
        editor: null,
        shouldRender: false
    }),
    computed: {
        cleanContent() {
            let content = this.value;

            if (content && typeof content === 'object') {
                content = this.filterEmptyText(content);
            }

            return content;
        },
        isEmpty() {
            if (!this.editor || this.editor.isEmpty) {
                return true;
            }

            if (!getKeys(this.editor.getJSON().content).includes('text')) {
                return true;
            }

            return false;
        },
        wysiwygClassList() {
            let classList = [];

            classList.push('app-wysiwyg');

            if (this.editor && this.editor.isFocused) {
                classList.push('app-wysiwyg--active');
            }

            if (this.inline) {
                classList.push('app-wysiwyg--inline');
            }

            if (this.disabled) {
                classList.push('app-wysiwyg--disabled');
            }

            if (this.editable) {
                classList.push('app-wysiwyg--editable');
            } else {
                classList.push('app-wysiwyg--readonly');
            }

            if (!this.isValid) {
                classList.push('app-wysiwyg--error');
            }

            return classList.join(' ');
        },
        toolbarClassList() {
            let classList = [];

            classList.push('app-wysiwyg__toolbar');

            if (this.inline) {
                classList.push('app-wysiwyg__toolbar--floating');
                classList.push('elevation-5');
            }

            return classList.join(' ');
        }
    },
    watch: {
        value(value) {
            if (!this.editor) {
                return;
            }

            const valueString = value ? JSON.stringify(value) : null;
            const isSame = this.editor.getJSON().toString() === valueString;

            if (isSame) {
                return;
            }

            const { from, to } = this.editor.state.selection;
            this.editor.commands.setContent(this.cleanContent, false);
            this.editor.commands.setTextSelection({ from, to });
        },
        editable(v) {
            if (this.editor) {
                this.editor.destroy();
            }

            this.editor = createEditor({
                editable: v && !this.disabled,
                content: this.cleanContent,
                onUpdate: this.onUpdate,
                onFocus: this.onFocus,
                onBlur: this.onBlur
            });
        },
        disabled(v) {
            if (this.editor) {
                this.editor.destroy();
            }

            this.editor = createEditor({
                editable: this.editable && !v,
                content: this.cleanContent,
                onUpdate: this.onUpdate,
                onFocus: this.onFocus,
                onBlur: this.onBlur
            });
        }
    },
    async mounted() {
        this.editor = createEditor({
            editable: this.editable && !this.disabled,
            content: this.cleanContent,
            onUpdate: this.onUpdate,
            onFocus: this.onFocus,
            onBlur: this.onBlur
        });
        await wait(10);
        this.shouldRender = true;
    },
    beforeDestroy() {
        this.shouldRender = false;
        if (this.editor) {
            this.editor.destroy();
        }
    },
    methods: {
        onUpdate() {
            this.$emit('input', this.isEmpty ? null : this.editor.getJSON());
            this.$emit('validate', this.editor.commands.validate());
        },
        onFocus() {
            this.isFocused = true;
        },
        onBlur() {
            this.isFocused = false;
        },
        filterEmptyText(item) {
            if (item && typeof item === 'object' && item.type === 'text' && !item.text.trim()) {
                return null;
            }

            if (item && Array.isArray(item)) {
                return item.map(node => this.filterEmptyText(node)).filter(Boolean);
            }

            if (item && typeof item === 'object') {
                for (const key in item) {
                    if (item[`${key}`] && Array.isArray(item[`${key}`])) {
                        item[`${key}`] = this.filterEmptyText(item[`${key}`]).filter(Boolean);
                    }
                }
                return item;
            }

            return item;
        }
    }
};
</script>

<style lang="scss">
.v-application .app-wysiwyg {
    display: block;
    position: relative;
    border: 2px solid var(--v-background-darken4);
    border-radius: 4px;
    padding-bottom: 3rem;
    min-width: 25rem;
    max-width: 100%;

    &--active:not(.app-wysiwyg--inline) {
        border-color: var(--v-primaryText-base);
    }

    &--inline {
        border: none;
        border-radius: 0;
        height: auto;
        padding-bottom: 0;
    }

    &--readonly {
        border: none;
        border-radius: 0;
        min-width: 8rem;
        padding-bottom: 0;
        .app-wysiwyg {
            &__content {
                padding-bottom: 0;
                height: auto;
            }
        }
    }

    &--editable {
        background: var(--v-background-base);
    }

    &--inline {
        table .tiptap-input__options {
            display: none;
        }
    }

    &__validity {
        position: absolute;
        top: 0.5rem;
        right: 0.5rem;
        z-index: 2;
    }

    &__toolbar {
        display: flex;
        flex-direction: row;
        align-items: center;
        flex-wrap: nowrap;
        width: max-content;
        height: 3rem;
        padding: 0 0.25rem;
        .v-item-group {
            width: 100%;
        }
    }

    &__toolbar:not(.app-wysiwyg__toolbar--floating) {
        position: absolute;
        bottom: 0;
        right: 0;
        left: 0;
        width: 100%;
        background: var(--v-background-base);
        z-index: 1;
    }

    &__toolbar--floating {
        background: var(--v-background-base);
        border: 1px solid var(--v-background-darken4);
        border-radius: 999px;
    }

    &__toolbar--hidden {
        display: none;
    }

    &__group {
        padding: 0 0.5rem;
        button {
            margin: 0 0.4rem;
        }
        &:not(:last-child) {
            border-right: 0.1rem solid var(--v-background-darken3);
        }
    }

    // &__button {
    //     &--active button {
    //         color: var(--v-primaryBackground-base) !important;
    //     }
    // }

    &__content {
        .ProseMirror {
            height: 100%;
            p:last-child {
                margin-bottom: 0;
            }
        }
    }

    &__content:not(.app-wysiwyg--inline .app-wysiwyg__content):not(.app-wysiwyg--readonly .app-wysiwyg__content) {
        height: 100%;
        padding-bottom: 3rem;
    }

    table {
        border-collapse: collapse;
        margin: 0;
        overflow: hidden;
        table-layout: fixed;
        width: 100%;
        box-sizing: border-box;
        min-width: 1rem;
        padding: 0.3rem 1rem;
        position: relative;
        vertical-align: top;
        th,
        td {
            box-sizing: border-box;
            min-width: 1rem;
            padding: 0.3rem 1rem;
            position: relative;
            vertical-align: top;
            border: 2px solid var(--v-primaryLight-base);
        }
        th {
            font-size: 0.9rem;
            font-weight: 600;
            color: var(--v-primaryText-base);
            text-align: left;
            border-bottom: 2px solid var(--v-primaryLight-base);
            background: var(--v-background-darken1);
            p {
                margin-bottom: 0;
            }
        }
        td > *,
        th > * {
            margin-bottom: 0;
        }
        .column-resize-handle {
            background-color: var(--v-primaryBackground-base);
            bottom: -2px;
            position: absolute;
            right: -2px;
            pointer-events: none;
            top: 0;
            width: 4px;
        }
    }

    *[contenteditable='true'] {
        border-radius: 0;
        border: none;
        &:focus {
            border: none;
        }
    }

    *[readonly='true'] {
        cursor: default;
    }
}

.ProseMirror {
    &-focused {
        outline: 0;
    }

    p.is-editor-empty:first-child::before {
        content: attr(data-placeholder);
        float: left;
        color: rgba(0, 0, 0, 0.4);
        pointer-events: none;
        height: 0;
    }

    > * + * {
        margin-top: 0.75em;
    }

    ul,
    ol {
        padding: 0 1rem;
        li {
            margin: 0.3rem 0;
        }
        ul,
        ol {
            margin-bottom: 0;
        }
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        line-height: 1.1;
        font-weight: normal;
        margin-bottom: 0.5rem;
    }

    code {
        background-color: rgba(#616161, 0.1);
        color: #616161;
    }

    pre {
        background: #0d0d0d;
        color: #fff;

        padding: 0.75rem 1rem;
        border-radius: 0.5rem;

        code {
            color: inherit;
            padding: 0;
            background: none;
            font-size: 0.8rem;
        }
    }

    img {
        max-width: 100%;
        height: auto;
    }

    blockquote {
        border-left: 0.3rem solid var(--v-background-darken4);
        background: var(--v-background-darken1);
        padding: 0.5rem 0.5rem 0.5rem 1rem;
        border-radius: 4px;
        *:last-child {
            margin-bottom: 0;
        }
    }

    hr {
        border: none;
        border-top: 2px solid var(--v-background-darken2);
        margin: 2rem 0;
    }

    ul {
        margin-bottom: 1rem;
        p {
            margin-bottom: 0;
        }
    }

    ul[data-type='taskList'] {
        padding: 0 0 0 1rem;
        list-style: none;
        li {
            display: flex;
        }
        label {
            margin-right: 0.5rem;
        }
    }

    &.resize-cursor {
        cursor: col-resize;
    }
}

.ProseMirror:not(.app-wysiwyg--inline .ProseMirror):not(.app-wysiwyg--readonly .ProseMirror) {
    padding: 1rem 1.4rem;
}

*[contenteditable='true'] {
    outline: none;
    border-radius: 4px;
    border: 1px solid var(--v-background-darken4);
    &:focus {
        border: 1px solid var(--v-primaryText-base);
    }
}
</style>
